import React, { useEffect, useState } from "react";
import { api, fileToBase64 } from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const RECEIPT_DEFAULTS = {
  receipt_terms: "",
  receipt_terms_font_size: "xs",
  receipt_show_logo: true,
  receipt_show_address: true,
  receipt_show_phone: true,
  receipt_show_cnpj: true,
  receipt_show_terms: true,
  receipt_show_customer: true,
};

const PIX_DEFAULTS = {
  pix_key: "",
  pix_key_type: "",
  pix_holder_name: "",
  pix_city: "",
};

export default function Loja() {
  const [data, setData] = useState({
    name: "", cnpj: "", address: "", socials: "", instagram: "", logo: "", phone: "", about: "",
    ...RECEIPT_DEFAULTS, ...PIX_DEFAULTS,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/store").then(r => setData(p => ({ ...p, ...RECEIPT_DEFAULTS, ...PIX_DEFAULTS, ...r.data })))
       .catch((e) => { try { console.error("Erro ao carregar loja:", e); } catch {} });
  }, []);

  const update = (k, v) => setData(p => ({ ...p, [k]: v }));

  const handleLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const b64 = await fileToBase64(f);
    update("logo", b64);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/store", data);
      toast.success("Loja salva");
    } catch (e) {
      toast.error("Erro ao salvar");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="loja-page">
      <div>
        <p className="label-tiny mb-2">Configurações</p>
        <h1 className="font-display text-4xl tracking-tight">Dados da Loja</h1>
      </div>

      <Tabs defaultValue="dados">
        <TabsList>
          <TabsTrigger value="dados" data-testid="loja-tab-dados">Dados gerais</TabsTrigger>
          <TabsTrigger value="recibo" data-testid="loja-tab-recibo">Configurar recibo (venda)</TabsTrigger>
          <TabsTrigger value="pix" data-testid="loja-tab-pix">PIX (interno)</TabsTrigger>
        </TabsList>

        <TabsContent value="dados" className="mt-4">
          <Card className="p-8 border-[#E5E5E5] shadow-none">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>Nome *</Label>
                <Input value={data.name} onChange={e => update("name", e.target.value)} data-testid="store-name-input" />
              </div>
              <div>
                <Label>CNPJ</Label>
                <Input value={data.cnpj} onChange={e => update("cnpj", e.target.value)} data-testid="store-cnpj-input"/>
              </div>
              <div className="md:col-span-2">
                <Label>Endereço *</Label>
                <Input value={data.address} onChange={e => update("address", e.target.value)} data-testid="store-address-input"/>
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={data.phone} onChange={e => update("phone", e.target.value)} data-testid="store-phone-input"/>
              </div>
              <div>
                <Label>Instagram (sem @)</Label>
                <Input value={data.instagram || ""} onChange={e => update("instagram", e.target.value.replace("@",""))} placeholder="minhaloja" data-testid="store-instagram-input"/>
              </div>
              <div>
                <Label>Outras redes / observação</Label>
                <Input value={data.socials} onChange={e => update("socials", e.target.value)} placeholder="@minhaloja" data-testid="store-socials-input"/>
              </div>
              <div className="md:col-span-2">
                <Label>Sobre a loja</Label>
                <Textarea value={data.about} onChange={e => update("about", e.target.value)} rows={3} data-testid="store-about-input"/>
              </div>
              <div className="md:col-span-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-4 mt-1">
                  {data.logo ? (
                    <img src={data.logo} alt="Logo" className="w-20 h-20 object-cover rounded border border-[#E5E5E5]" />
                  ) : (
                    <div className="w-20 h-20 rounded border border-dashed border-[#E5E5E5] flex items-center justify-center text-xs text-neutral-400">Sem logo</div>
                  )}
                  <Input type="file" accept="image/*" onChange={handleLogo} data-testid="store-logo-input"/>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="recibo" className="mt-4">
          <Card className="p-8 border-[#E5E5E5] shadow-none space-y-6" data-testid="receipt-config-card">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Termos do recibo</Label>
              <Textarea
                value={data.receipt_terms || ""}
                onChange={(e)=>update("receipt_terms", e.target.value)}
                rows={8}
                placeholder="Cole aqui as condições e termos de garantia que aparecerão no rodapé do recibo de venda. Ex.: 'Trocas em até 7 dias com nota; produto deve estar em embalagem original; garantia legal de 90 dias…'"
                data-testid="receipt-terms-input"
              />
              <p className="text-[11px] text-neutral-500 mt-1">Esse texto fixo aparece em TODOS os recibos de venda (não interfere na assistência, que tem seus próprios termos).</p>

              <div className="mt-4">
                <p className="text-xs uppercase tracking-wider text-neutral-500 font-medium mb-2">Tamanho da fonte no recibo</p>
                <div className="flex gap-1.5 flex-wrap">
                  {[{k:"xxxs",l:"Ultra pequeno"},{k:"xxs",l:"Bem pequeno"},{k:"xs",l:"Pequena"},{k:"sm",l:"Média"},{k:"md",l:"Grande"},{k:"lg",l:"Muito grande"}].map(o => (
                    <button
                      key={o.k}
                      type="button"
                      onClick={()=>update("receipt_terms_font_size", o.k)}
                      className={`text-xs px-3 py-1.5 rounded-md border transition ${(data.receipt_terms_font_size || "xs") === o.k ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]" : "bg-white border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:border-white/10 dark:text-neutral-300"}`}
                      data-testid={`venda-font-${o.k}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
                {data.receipt_terms && (
                  <div className={`mt-2 p-2 border border-dashed border-[#E5E5E5] rounded text-neutral-700 ${({xxxs:'text-[6px]',xxs:'text-[8px]',xs:'text-[9px]',sm:'text-[11px]',md:'text-[13px]',lg:'text-[15px]'})[data.receipt_terms_font_size || "xs"]} whitespace-pre-line max-h-24 overflow-y-auto`}>
                    <div className="font-medium mb-1 text-[10px] uppercase tracking-wider">Termos e Condições</div>
                    {data.receipt_terms}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">O que mostrar no recibo</Label>
              <div className="mt-3 space-y-3 border border-[#E5E5E5] rounded-md p-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="rsl">Logo da loja</Label>
                  <Switch id="rsl" checked={!!data.receipt_show_logo} onCheckedChange={(v)=>update("receipt_show_logo", v)} data-testid="receipt-show-logo"/>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="rsa">Endereço</Label>
                  <Switch id="rsa" checked={!!data.receipt_show_address} onCheckedChange={(v)=>update("receipt_show_address", v)} data-testid="receipt-show-address"/>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="rsp">Telefone</Label>
                  <Switch id="rsp" checked={!!data.receipt_show_phone} onCheckedChange={(v)=>update("receipt_show_phone", v)} data-testid="receipt-show-phone"/>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="rsj">CNPJ</Label>
                  <Switch id="rsj" checked={!!data.receipt_show_cnpj} onCheckedChange={(v)=>update("receipt_show_cnpj", v)} data-testid="receipt-show-cnpj"/>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="rsc">Dados do cliente</Label>
                  <Switch id="rsc" checked={!!data.receipt_show_customer} onCheckedChange={(v)=>update("receipt_show_customer", v)} data-testid="receipt-show-customer"/>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="rst">Termos / condições no rodapé</Label>
                  <Switch id="rst" checked={!!data.receipt_show_terms} onCheckedChange={(v)=>update("receipt_show_terms", v)} data-testid="receipt-show-terms"/>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="pix" className="mt-4">
          <Card className="p-8 border-[#E5E5E5] shadow-none space-y-6" data-testid="pix-config-card">
            <div>
              <p className="label-tiny">Recebimento via PIX</p>
              <h2 className="font-display text-2xl tracking-tight mt-1">Chave PIX</h2>
              <p className="text-xs text-neutral-500 mt-2">
                Essa chave é <strong>apenas interna</strong> — não aparece no catálogo público nem nos recibos. Ela é usada para gerar o
                link de pagamento ("Copia e Cola") que você envia no WhatsApp quando o pedido tiver pagamento em PIX.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>Tipo da chave</Label>
                <Select value={data.pix_key_type || ""} onValueChange={(v)=>update("pix_key_type", v)}>
                  <SelectTrigger data-testid="pix-key-type"><SelectValue placeholder="Selecione o tipo"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cpf">CPF</SelectItem>
                    <SelectItem value="cnpj">CNPJ</SelectItem>
                    <SelectItem value="email">E-mail</SelectItem>
                    <SelectItem value="phone">Telefone</SelectItem>
                    <SelectItem value="random">Chave aleatória</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Chave PIX</Label>
                <Input
                  value={data.pix_key || ""}
                  onChange={(e)=>update("pix_key", e.target.value)}
                  placeholder="Ex.: email@dominio.com / 11999999999 / 000.000.000-00"
                  data-testid="pix-key-input"
                />
              </div>
              <div>
                <Label>Nome do titular (aparece no banco do cliente)</Label>
                <Input
                  value={data.pix_holder_name || ""}
                  onChange={(e)=>update("pix_holder_name", e.target.value)}
                  placeholder="NOME COMPLETO OU RAZAO SOCIAL"
                  data-testid="pix-holder-input"
                />
                <p className="text-[11px] text-neutral-500 mt-1">Sem acentos, até 25 caracteres.</p>
              </div>
              <div>
                <Label>Cidade</Label>
                <Input
                  value={data.pix_city || ""}
                  onChange={(e)=>update("pix_city", e.target.value)}
                  placeholder="SAO PAULO"
                  data-testid="pix-city-input"
                />
                <p className="text-[11px] text-neutral-500 mt-1">Sem acentos, até 15 caracteres.</p>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="store-save-button">
          {busy ? "Salvando…" : "Salvar alterações"}
        </Button>
      </div>
    </div>
  );
}
